import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { IoSearchOutline, IoFilterOutline, IoAddOutline, IoTimeOutline, IoPlayOutline, IoCheckmarkCircleOutline, IoInformationCircleOutline } from "react-icons/io5";

import DebounceInput from "../../../../../../components/DebounceInput";
import api from "../../../../../../services/api";
import MultiSelect from "../../../../../../components/MultiSelect";
import { MultiAction } from "./MultiAction";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "@/components/modal";
import TipTapEditor from "@/components/TipTapEditor";
import DateRangeFilter from "./DateRangeFilter";

const TopMenu = ({ filters, setFilters, project, items, selectedItems, onTicketCreated, onMultipleDelete, calculateEstimation }) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const handleMultipleDelete = async () => {
    try {
      if (!window.confirm("Are you sure you want to delete selected items?")) return;

      for (let i = 0; i < selectedItems.length; i++) {
        const { ok } = await api.remove(`/roadlog_item/${selectedItems[i]._id}`);
        if (!ok) {
          toast.error(`Failed to delete item ${selectedItems[i]._id}`);
          continue;
        }
      }

      toast.success("Items deleted successfully");
      onMultipleDelete();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete items");
    }
  };

  const handleTicketCreated = (newTicket) => {
    setIsCreateTicketModalOpen(false);
    if (newTicket.category === "BUG") {
      query.set("item_modal_id", newTicket._id);
      return navigate(`/project/${project._id}/roadlog/backlog?${query.toString()}`);
    }
    query.set("item_modal_id", newTicket._id);
    navigate({ search: query.toString() });
    onTicketCreated(newTicket);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-3 flex items-center gap-3 border-b border-gray-100">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <IoSearchOutline className="h-4 w-4 text-gray-400" />
          </div>
          <DebounceInput
            debounce={300}
            className="block w-full pl-10 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search items..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="gap-2 w-44">
            <SelectModule value={filters.module_ids} onChange={(modules) => setFilters((f) => ({ ...f, module_ids: modules }))} project={project} />
          </div>
          <div className="gap-2 w-44">
            <SelectTag value={filters.roadlog_tags} onChange={(tags) => setFilters((f) => ({ ...f, roadlog_tags: tags }))} project={project} />
          </div>
          <div className="gap-2 w-44">
            <MultiSelect
              id="select-status"
              options={["TODO", "READY_TO_DEV", "IN_PROGRESS", "DONE", "NEED_INFO", "ARCHIVED"].map((status) => ({
                value: status,
                label: status,
                icon: getStatusIcon(status),
              }))}
              values={filters.statuses?.map((status) => ({ value: status, label: status }))}
              onSelectedChange={(e) => setFilters((f) => ({ ...f, statuses: e.map((e) => e.value) }))}
              placeholder="Status"
              buttonClassName="px-3 py-1.5"
            />
          </div>

          <button
            onClick={() => setShowMoreFilters(!showMoreFilters)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md transition-colors
              ${showMoreFilters ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>
            <IoFilterOutline className="h-4 w-4" />
            More Filters
          </button>

          <div className="flex items-center gap-2 pl-2 border-l border-gray-200 ">
            <button
              onClick={() => setIsCreateTicketModalOpen(true)}
              className="inline-flex w-full items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
              <IoAddOutline className="h-4 w-4" />
              New Item
            </button>
            <MultiAction selectedItems={selectedItems} items={items} project={project} handleMultipleDelete={handleMultipleDelete} calculateEstimation={calculateEstimation} />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showMoreFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-100">
            <div className="p-3 grid grid-cols-4 gap-3">
              <MultiSelect
                id="select-resource-profile"
                options={["DEVELOPER", "EXPERIMENTED DEVELOPER", "CTO", "PRODUCT", "IA ENGINEER", "DevOps"].map((r) => ({
                  value: r,
                  label: r,
                }))}
                values={filters.resource_profile?.map((r) => ({ value: r, label: r }))}
                onSelectedChange={(e) => setFilters((f) => ({ ...f, resource_profile: e.map((e) => e.value) }))}
                placeholder="Resource Profile"
                className="w-full"
                buttonClassName="px-3 py-1.5"
              />

              <div className="w-full">
                <SelectBudgets value={filters.budget_id} onChange={(e) => setFilters((f) => ({ ...f, budget_id: e }))} project={project} />
              </div>

              <MultiSelect
                id="select-nnl"
                options={["NOW", "NEXT", "LATER"].map((p) => ({
                  value: p,
                  label: p,
                  className: getNNLClass(p),
                }))}
                values={filters.nnls?.map((p) => ({ value: p, label: p }))}
                onSelectedChange={(e) => setFilters((f) => ({ ...f, nnls: e.map((e) => e.value) }))}
                placeholder="NNL"
                className="w-full"
                buttonClassName="px-3 py-1.5"
              />
              <select
                value={filters.is_in_backlog || ""}
                onChange={(e) => setFilters((f) => ({ ...f, is_in_backlog: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option value="">All Backlog Status</option>
                <option value="yes">In Backlog</option>
                <option value="no">Not in Backlog</option>
              </select>
              <DateRangeFilter filters={filters} setFilters={setFilters} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <CreateTicket isOpen={isCreateTicketModalOpen} onClose={() => setIsCreateTicketModalOpen(false)} project={project} onTicketCreated={handleTicketCreated} />
    </div>
  );
};

const getStatusIcon = (status) => {
  switch (status) {
    case "TODO":
      return <IoTimeOutline className="h-4 w-4" />;
    case "IN_PROGRESS":
      return <IoPlayOutline className="h-4 w-4" />;
    case "DONE":
      return <IoCheckmarkCircleOutline className="h-4 w-4" />;
    case "NEED_INFO":
      return <IoInformationCircleOutline className="h-4 w-4" />;
    default:
      return null;
  }
};

const getNNLClass = (nnlValue) => {
  switch (nnlValue) {
    case "NOW":
      return "bg-blue-50 text-blue-700";
    case "NEXT":
      return "bg-yellow-50 text-yellow-700";
    case "LATER":
      return "bg-gray-50 text-gray-700";
    default:
      return "";
  }
};

const SelectModule = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);
  const [lastFetch, setLastFetch] = useState(0);

  async function fetchModules() {
    const { data } = await api.post("/roadlog_module/search", { project_id: project._id });
    setOptions(data);
    setLastFetch(Date.now());
  }

  useEffect(() => {
    fetchModules();
  }, [project._id]);

  useEffect(() => {
    function handleFocus() {
      if (Date.now() - lastFetch > 5000) {
        fetchModules();
      }
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastFetch]);

  const selectedValues = Array.isArray(value) ? value.map((v) => ({ value: v, label: v })) : value ? [{ value, label: value }] : [];
  return (
    <MultiSelect
      id="select-module"
      options={options.map((o) => ({ value: o._id, label: o.title }))}
      values={selectedValues}
      onSelectedChange={(selected) => onChange(selected.map((s) => s.value))}
      placeholder="Module"
      buttonClassName="px-3 py-1.5"
    />
  );
};

const SelectTag = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);

  async function fetchTags() {
    const { data } = await api.post("/roadlog_tag/search", { project_id: project._id, sort: "name" });
    setOptions(data);
  }

  useEffect(() => {
    fetchTags();
  }, [project._id]);

  const selectedValues = Array.isArray(value) ? value.map((v) => ({ value: v._id, label: v.name })) : value ? [{ value: value._id, label: value.name }] : [];
  return (
    <MultiSelect
      id="select-tag"
      options={options.map((o) => ({ value: o._id, label: o.name }))}
      values={selectedValues}
      onSelectedChange={(selected) => onChange(selected.map((s) => ({ _id: s.value, name: s.label })))}
      placeholder="Tag"
      buttonClassName="px-3 py-1.5"
      onOpen={() => fetchTags()}
    />
  );
};

const SelectBudgets = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);

  async function fetchBudgets() {
    const { data } = await api.post("/budget/search", { projectId: project._id, status: "active" });
    setOptions(data);
  }

  useEffect(() => {
    fetchBudgets();
  }, [project._id]);

  return (
    <select
      className={`w-full px-3 py-1.5 justify-between items-center gap-4 border border-gray-200 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}>
      <option value="">All budgets</option>
      {options.map((option) => (
        <option key={option._id} value={option._id}>
          {option.name}
        </option>
      ))}
    </select>
  );
};

const CreateTicket = ({ isOpen, onClose, project, onTicketCreated }) => {
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState({ description: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (ticket.description.trim() === "") return;

    try {
      setLoading(true);
      const payload = {
        ...ticket,
        project_id: project._id,
      };

      const { data, ok } = await api.post("/roadlog_item", payload);
      if (!ok) throw new Error("Failed to create ticket");

      onTicketCreated(data);
      setTicket({ description: "" });
      toast.success("Ticket created successfully");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-5xl">
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-t-lg border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">Create New Ticket</h2>
        <p className="mt-1 text-sm text-gray-600">
          If this is a bug, it will be automatically categorized and placed in the backlog. Otherwise, it will be added to the roadmap for prioritization.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6 p-6">
        <div className="space-y-2">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description <span className="text-red-500">*</span>
          </label>
          <div className="rounded-xl overflow-hidden border border-gray-300">
            <div className="bg-white">
              <TipTapEditor
                buttonList={["bold", "italic", "link", "bulletList", "orderedList", "blockquote", "codeBlock"]}
                values={ticket.description}
                onChange={(value) => setTicket((prev) => ({ ...prev, description: value }))}
                onSubmit={() => {
                  if (ticket.description.trim() === "") return;
                  handleSubmit({ preventDefault: () => {} });
                }}
                placeholder="Provide detailed information about the ticket..."
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Press Enter to submit, Shift + Enter for new line</p>
        </div>
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={loading || ticket.description.trim() === ""}
            className="px-6 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : (
              "Create Ticket"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TopMenu;
